import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconX } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog';
import { useAuthStore } from '@/store/authStore';
import { useFbPromoStore } from '@/store/fbPromoStore';
import usageService from '@/services/usageService';
import { getCookie, setCookie, todayLocalDate } from '@/lib/cookies';
import { CONTEST_SITE_URL } from '@/config/contestSite';

// Contest announcement artwork shown in the modal (square, 1080×1080).
const CONTEST_IMAGE = '/images/fb/contest.webp';

// Device-local date the modal was last SHOWN (value: `YYYY-MM-DD`). Stamped the
// moment the modal opens — not on close — so one appearance per calendar day is
// guaranteed no matter whether the user closes it, clicks through, or just
// reloads. Written to BOTH a cookie and localStorage under the same key: the
// localStorage write fires the cross-tab `storage` event, which other open tabs
// use to hide their copy of the modal immediately.
const COOKIE_KEY = 'sk_fb_promo_last_shown';

// Mark the modal as shown for today (cookie + localStorage, see above).
const stampToday = () => {
    const today = todayLocalDate();
    setCookie(COOKIE_KEY, today);
    try { localStorage.setItem(COOKIE_KEY, today); } catch { /* storage may be unavailable */ }
};

// Has any tab on this device already shown the modal today?
const stampedToday = () => {
    const today = todayLocalDate();
    let ls = null;
    try { ls = localStorage.getItem(COOKIE_KEY); } catch { /* storage may be unavailable */ }
    return getCookie(COOKIE_KEY) === today || ls === today;
};

/**
 * Once-a-day promo modal (the name is historical: it used to be the "Follow SohozKaj on Facebook" popup).
 * Today it is just the contest artwork; the whole image is a link to the contest site (CONTEST_SITE_URL,
 * config/contestSite.js) and the only other control is the close button.
 * Mounted globally (see RouteAwareFloats in App.jsx). It shows on ANY page when:
 *   - the user is authenticated,
 *   - the backend `showAfterLoginModal` Setting flag is true, and
 *   - it hasn't been SHOWN yet today on this device (device-local day, tracked in
 *     a cookie + localStorage; the day is stamped on show, not on close).
 * The link is a plain anchor — no SDK or embed.
 */
export default function FacebookPromoModal() {
    const { t } = useTranslation();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const { isOpen, openModal, closeModal } = useFbPromoStore();

    const checkedRef = useRef(false); // evaluate the show-decision only once per app load

    // ── Decide whether to open (on any page, once the gates pass) ──
    useEffect(() => {
        if (checkedRef.current) return;
        if (!isAuthenticated) return;
        checkedRef.current = true;

        if (stampedToday()) return;   // already shown today (any tab)

        // No cancel flag: this component is mounted for the app's lifetime, and under
        // React 18 StrictMode a cleanup-driven cancel would abort the only fetch
        // (checkedRef already prevents a duplicate). openModal() is a harmless state set.
        usageService
            .getCreditConfig()
            .then((cfg) => {
                if (cfg?.showAfterLoginModal !== true) return;
                if (stampedToday()) return; // another tab showed it while the config loaded
                // Stamp the day the moment the modal opens — regardless of whether the
                // user closes it, clicks through, or just reloads, it won't show again today.
                stampToday();
                openModal();
            })
            .catch(() => { /* never block the app on a config fetch */ });
    }, [isAuthenticated, openModal]);

    // Cross-tab sync: when another tab stamps today (it just showed or closed the
    // modal), hide the copy in this tab too. `storage` only fires in OTHER tabs,
    // so the tab that is legitimately displaying the modal keeps it open.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === COOKIE_KEY && e.newValue === todayLocalDate()) closeModal();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [closeModal]);

    // Any dismissal (red X, Esc, or clicking the image) hides the modal and
    // re-stamps today. Outside-overlay clicks are deliberately ignored (see
    // onInteractOutside below) so the user makes an explicit choice.
    const handleClose = () => {
        stampToday();
        closeModal();
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent
                aria-describedby={undefined}
                showClose={false}
                onInteractOutside={(e) => e.preventDefault()}
                // z-[100]: above the sticky page header (z-[60] below the 1024px breakpoint), which otherwise
                // paints over the top of the modal and clips the close button.
                overlayClassName="z-[100]"
                className="z-[100] w-[min(780px,calc(100vw-1.5rem),92dvh)] max-w-none p-0 rounded-2xl border border-zinc-200 dark:border-zinc-700"
            >
                <DialogTitle className="sr-only">{t('fbPromo.contestTitle')}</DialogTitle>

                {/* Close — Esc dismisses too (outside clicks don't); every path goes
                    through handleClose so the day is always stamped. Anchored to the
                    dialog shell (not the scroll body), so it stays reachable while the
                    stacked mobile content scrolls underneath. */}
                <DialogClose className="absolute right-2.5 top-2.5 sm:right-3 sm:top-3 z-20 inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-400/60">
                    <IconX size={16} />
                    <span className="sr-only">{t('common.close')}</span>
                </DialogClose>

                {/* The entire modal is the link: tapping anywhere on the artwork opens the contest site in
                    a new tab and dismisses the modal (stamping today). Only the close button sits above it. */}
                <a
                    href={CONTEST_SITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleClose}
                    className="block rounded-2xl overflow-hidden"
                >
                    <img
                        src={CONTEST_IMAGE}
                        alt={t('fbPromo.contestAlt')}
                        className="block w-full h-auto max-h-[92dvh] object-contain"
                    />
                </a>
            </DialogContent>
        </Dialog>
    );
}
